import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2, Mail, Phone, MapPin } from 'lucide-react';
import { SITE, IMAGES, INTEREST_OPTIONS, REACHABILITY_OPTIONS } from '../mock';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kontakt() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', interests: [], reachability: [] });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const toggleArr = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.name || !form.email || !form.phone || !form.message) {
      setStatus('error');
      setErrorMsg('Bitte fülle alle Pflichtfelder aus.');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
    }
  };

  return (
    <div>
      <SEO
        path="/kontakt"
        title="Kontakt – Persönliche Beratung anfragen"
        description="Vereinbare ein unverbindliches Erstgespräch mit Pascal Schwab. Persönlich, schnell, individuell – üblicherweise Antwort innerhalb von 24 Stunden."
      />
      <section className="pt-36 pb-12 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-4">Kontakt</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-[#f5efe3] leading-[1.05]">
            Get in <span className="italic text-gold-grad">Touch</span>
          </h1>
          <p className="mt-6 text-[#9a9285] text-lg max-w-2xl mx-auto">
            Ich bin gespannt darauf, Dich kennenzulernen. Fülle einfach das Formular aus – ich melde mich so schnell wie möglich.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl overflow-hidden border border-[#d4b574]/15">
              <img src={IMAGES.pascalProfile} alt="Pascal Schwab" className="w-full h-auto" />
            </div>
            <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-[#d4b574] mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">E-Mail</div>
                  <a href={`mailto:${SITE.email}`} className="text-[#e8d5a8] hover:underline">{SITE.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#d4b574] mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Standort</div>
                  <div className="text-[#cfc6b5]">Schweiz</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-[#d4b574] mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Erreichbarkeit</div>
                  <div className="text-[#cfc6b5]">Werktags & Wochenende, nach Vereinbarung</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {status === 'success' ? (
              <div className="p-10 rounded-2xl bg-[#d4b574]/5 border border-[#d4b574]/25">
                <CheckCircle2 className="text-[#e8d5a8]" size={36} />
                <h3 className="font-serif-display text-3xl text-[#e8d5a8] mt-4">Vielen Dank!</h3>
                <p className="text-[#cfc6b5] mt-2">Deine Nachricht ist bei mir eingegangen. Ich melde mich in Kürze bei dir.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5 p-8 rounded-2xl border border-white/8 bg-[#0e0e0c]">
                <p className="text-xs text-[#7a7060]">* gibt ein Pflichtfeld an</p>
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Name + Vorname *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 transition" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">E-Mail *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 transition" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Telefonnummer *</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 transition" />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Ich interessiere mich für * <span className="normal-case tracking-normal text-[#7a7060]">(Mehrfachauswahl möglich)</span></label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleArr('interests', opt)}
                        className={`px-4 py-2 rounded-full text-xs border transition-all ${
                          form.interests.includes(opt)
                            ? 'bg-[#d4b574] text-[#0a0a0c] border-[#d4b574]'
                            : 'border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Ich bin am besten erreichbar * <span className="normal-case tracking-normal text-[#7a7060]">(Mehrfachauswahl möglich)</span></label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {REACHABILITY_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleArr('reachability', opt)}
                        className={`px-4 py-2 rounded-full text-xs border transition-all ${
                          form.reachability.includes(opt)
                            ? 'bg-[#d4b574] text-[#0a0a0c] border-[#d4b574]'
                            : 'border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Nachricht *</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 transition resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-xs text-red-400">{errorMsg || 'Bitte fülle die Pflichtfelder aus.'}</p>
                )}

                <button type="submit" disabled={status === 'loading'} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors">
                  {status === 'loading' ? 'Wird gesendet…' : 'Bestätigen'} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
